/**
 * Which desk to open when somebody's roles change.
 *
 * The app remembers the desk you last had open, across sessions, and picks
 * one for you only when nothing is remembered. So somebody who had used
 * Tributary as a requester and was then made an agent in ANOTHER desk kept
 * opening the remembered one, where they really are only a requester — and
 * looked as though the grant had not worked until signing out cleared the
 * memory. (Bug report, QA 2026-10-07: two new agents saw "requester" until
 * they signed out and back in.)
 *
 * So the desks a person is staff in are remembered too. When that set gains
 * a desk and the one open is not one of them, the newly granted desk is
 * opened — once. A person who then chooses to sit in a desk as a requester
 * is never moved again for the same grant.
 */
export interface DeskRole {
  id: string;
  /** True where this person works tickets (agent and above). */
  staff: boolean;
}

export function deskOnRoleChange(args: {
  /** Desks they were staff in when last seen; null on the first visit since this existed. */
  before: readonly string[] | null;
  desks: readonly DeskRole[];
  current: string;
}): { switchTo: string | null; staffNow: string[] } {
  const staffNow = args.desks.filter((d) => d.staff).map((d) => d.id);
  const was = new Set(args.before ?? []);
  const gained = staffNow.filter((id) => !was.has(id));
  const here = args.desks.find((d) => d.id === args.current);
  const switchTo = gained.length > 0 && !(here?.staff ?? false) ? gained[0]! : null;
  return { switchTo, staffNow };
}
